import { CardStatusDock, ReasonType } from '@portal/models/enumerators/cards';
import { getInstance } from '@portal/repositories/securitySdkConfig';

const BASE_URL = '/cards';
const BASE_URL_V3 = '/v3/cards';

const CardsApi = {
  get: async (): Promise<bank.Card[]> => {
    const instance = getInstance();
    const { data } = await instance.get<bank.Card[]>(BASE_URL_V3);

    return data;
  },

  updateCardLimit: async (cardId: string, limitRequested: number) => {
    const instance = getInstance();
    await instance.patch(`${BASE_URL}/${cardId}/limit`, {
      limitRequested,
    });
  },

  requestDuplicate: async (
    cardId: string,
    description: ReasonType,
    pin: string
  ) => {
    const instance = getInstance();
    const { data } = await instance.post(
      `${BASE_URL_V3}/${cardId}/duplicates`,
      {
        description,
        authenticate: {
          pin,
        },
      }
    );

    return data;
  },

  getCardDetails: async (cardId: string): Promise<bank.Card> => {
    const instance = getInstance();
    const { data } = await instance.get<bank.Card>(`${BASE_URL_V3}/${cardId}`);

    return data;
  },

  blockCardV3: async (cardId: string, status: CardStatusDock, pin: string) => {
    const instance = getInstance();
    const { data } = await instance.post(`${BASE_URL_V3}/${cardId}/block`, {
      status,
      authenticate: {
        pin,
      },
    });

    return data;
  },

  unblockCardV3: async (cardId: string, pin: string) => {
    const instance = getInstance();
    const { data } = await instance.patch(`${BASE_URL_V3}/${cardId}/unblock`, {
      authenticate: {
        pin,
      },
    });

    return data;
  },

  cancelCardV3: async (
    cardId: string,
    description: ReasonType,
    pin: string
  ) => {
    const instance = getInstance();

    const { data } = await instance.post(
      `${BASE_URL_V3}/${cardId}/cancellations`,
      {
        description,
        authenticate: {
          pin,
        },
      }
    );

    return data;
  },

  changePasswordV3: async (
    cardId: string,
    newPassword: string,
    isUnlockedByIncorrectPassword: boolean,
    pin: string
  ) => {
    const instance = getInstance();
    const { data } = await instance.patch(`${BASE_URL_V3}/${cardId}/password`, {
      newPassword,
      isUnlockedByIncorrectPassword,
      authenticate: {
        pin,
      },
    });

    return data;
  },

  viewPasswordV3: async (cardId: string, pin: string) => {
    const instance = getInstance();
    const { data } = await instance.post(
      `${BASE_URL_V3}/${cardId}/password-views`,
      {
        authenticate: {
          pin,
        },
      }
    );

    return data;
  },

  createVirtualCardV3: async (expirationDate: string, pin: string) => {
    const instance = getInstance();
    const { data } = await instance.post(`${BASE_URL_V3}/virtual`, {
      expirationDate,
      authenticate: {
        pin,
      },
    });

    return data;
  },

  createPhysicalCardV3: async (pin: string) => {
    const instance = getInstance();
    const { data } = await instance.post<bank.Card>(`${BASE_URL_V3}/physical`, {
      authenticate: {
        pin,
      },
    });

    return data;
  },

  viewCardDataV3: async (cardId: string, pin: string) => {
    const instance = getInstance();
    const { data } = await instance.post(
      `${BASE_URL_V3}/${cardId}/sensitive-data`,
      {
        authenticate: {
          pin,
        },
      }
    );

    return data;
  },

  unlockFirstAccessV3: async (
    cardId: string,
    currentCardPassword: string,
    newCardPassword: string,
    pin: string
  ) => {
    const instance = getInstance();
    const { data } = await instance.post(
      `${BASE_URL_V3}/${cardId}/first-access`,
      {
        currentCardPassword,
        newCardPassword,
        authenticate: {
          pin,
        },
      }
    );

    return data;
  },

  unlockForIncorrectPasswordV3: async (cardId: string, pin: string) => {
    const instance = getInstance();
    const { data } = await instance.patch(
      `${BASE_URL_V3}/${cardId}/password/lock`,
      {
        authenticate: { pin },
      }
    );

    return data;
  },

  cardControlV3: async (
    cardId: string,
    pin: string,
    cardControl: bank.CardControl
  ) => {
    const instance = getInstance();
    const { data } = await instance.patch(`${BASE_URL_V3}/${cardId}/settings`, {
      authenticate: {
        pin,
      },
      cardControl,
    });

    return data;
  },

  verifyAccountDock: async () => {
    const instance = getInstance();
    const { data } = await instance.get(`/card-account`);

    return data;
  },

  createAccountDock: async () => {
    const instance = getInstance();
    const { data } = await instance.post(`/card-account`);

    return data;
  },

  trackCard: async (cardId: string) => {
    const instance = getInstance();
    const { data } = await instance.get(`${BASE_URL_V3}/${cardId}/tracking`);

    return data;
  },

  createSignatureHash: async (documentUrl: string) => {
    const instance = getInstance();
    const { data } = await instance.post(`${BASE_URL_V3}/signature-hashes`, {
      documentUrl,
    });

    return data;
  },

  confirmCardReceived: async (cardId: string): Promise<bank.Card> => {
    const instance = getInstance();
    const { data } = await instance.post<bank.Card>(
      `${BASE_URL_V3}/${cardId}/receipts`
    );

    return data;
  },

  validateOwnerShip: async (cardId: string) => {
    const instance = getInstance();
    const { data } = await instance.get(`${BASE_URL_V3}/${cardId}/ownership`);

    return data;
  },
};

export default CardsApi;
